import { useEffect, useState } from "react";
import {
  Table,
  Spinner,
  Button,
  Input,
  Form,
  FormGroup,
  Label,
} from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const FeePIFReporting = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterType, setFilterType] = useState("customerId");
  const [filterValue, setFilterValue] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const [y, m, d] = dateString.split("-");
    return `${d}-${m}-${y}`;
  };

  const getTodayDateISO = () => new Date().toISOString().split("T")[0];

  const fetchFeePIFReport = async (filters = {}) => {
    setLoading(true);
    try {
      const params = new URLSearchParams(filters).toString();
      const url = `${URL.REPORTING_FEE_PIF}${params ? `?${params}` : ""}`;
      const response = await axiosInstance.get(url);
      setRows(response.data || []);
    } catch (err) {
      console.error("Error fetching Fee PIF data:", err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFeePIFReport({});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const filters = {};
    const todayISO = getTodayDateISO();

    if (fromDate && fromDate > todayISO) {
      alert("From Date cannot be in the future!");
      return;
    }
    if (toDate && toDate > todayISO) {
      alert("To Date cannot be in the future!");
      return;
    }

    if (filterValue.trim()) filters[filterType] = filterValue.trim();

    if (fromDate && toDate) {
      if (fromDate > toDate) {
        alert("From Date cannot be after To Date.");
        return;
      }
      filters.startDate = formatDate(fromDate);
      filters.endDate = formatDate(toDate);
    }

    fetchFeePIFReport(filters);
  };

  const exportToExcel = () => {
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Fee_PIF_Report");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([excelBuffer], { type: "application/octet-stream" }),
      "Fee_PIF_Report.xlsx"
    );
  };

  const font12 = { fontSize: "10px" };
  const inputSmStyle = { ...font12, height: "30px", padding: "2px 6px" };
  const buttonSmStyle = {
    ...font12,
    padding: "4px 10px",
    height: "30px",
    lineHeight: "1",
  };

  const currentLabel =
    filterType === "customerId"
      ? "Customer ID"
      : filterType === "loanId"
      ? "Loan ID"
      : "EMI ID";

  return (
    <div style={font12}>
      <Form onSubmit={handleSearch} className="mb-3">
        <div
          className="d-flex align-items-end flex-wrap"
          style={{
            gap: "10px",
            overflowX: "auto",
            paddingBottom: "4px",
            whiteSpace: "nowrap",
          }}
        >
          <FormGroup className="mb-0" style={{ minWidth: "130px", ...font12 }}>
            <Label for="filterType" className="mb-1 fw-bold" style={font12}>
              Filter Type
            </Label>
            <Input
              bsSize="sm"
              type="select"
              id="filterType"
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={inputSmStyle}
            >
              <option value="customerId">Customer ID</option>
              <option value="loanId">Loan ID</option>
              <option value="emiId">EMI ID</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-0" style={{ minWidth: "160px", ...font12 }}>
            <Label for="filterValue" className="mb-1 fw-bold" style={font12}>
              {currentLabel}
            </Label>
            <Input
              bsSize="sm"
              id="filterValue"
              type="text"
              value={filterValue}
              onChange={(e) => setFilterValue(e.target.value)}
              placeholder={`Enter ${currentLabel}`}
              style={inputSmStyle}
            />
          </FormGroup>

          <FormGroup className="mb-0" style={{ minWidth: "150px", ...font12 }}>
            <Label for="fromDate" className="mb-1 fw-bold" style={font12}>
              From Date
            </Label>
            <Input
              bsSize="sm"
              id="fromDate"
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              style={inputSmStyle}
            />
          </FormGroup>

          <FormGroup className="mb-0" style={{ minWidth: "150px", ...font12 }}>
            <Label for="toDate" className="mb-1 fw-bold" style={font12}>
              To Date
            </Label>
            <Input
              bsSize="sm"
              id="toDate"
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              style={inputSmStyle}
            />
          </FormGroup>

          <div className="d-flex mb-0" style={{ gap: "6px" }}>
            <Button color="primary" type="submit" style={buttonSmStyle}>
              Search
            </Button>
            <Button
              type="button"
              color="success"
              onClick={exportToExcel}
              style={buttonSmStyle}
            >
              Export To Excel
            </Button>
          </div>
        </div>
      </Form>

      {loading ? (
        <div className="d-flex align-items-center mt-3" style={{ gap: 8 }}>
          <Spinner size="sm" /> <span>Loading Fee PIF report...</span>
        </div>
      ) : rows.length === 0 ? (
        <p className="mt-3 text-danger">No data present!</p>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table
            bordered
            striped
            hover
            responsive
            size="sm"
            className="mt-3 mb-0"
          >
            <thead className="thead-light">
              <tr>
                <th style={font12}>#</th>
                <th style={font12}>Loan ID</th>
                <th style={font12}>Customer ID</th>
                <th style={font12}>EMI ID</th>
                <th style={font12}>Type</th>
                <th style={font12}>Amount (₹)</th>
                <th style={font12}>Outstanding (₹)</th>
                <th style={font12}>Status</th>
                <th style={font12}>Create Date</th>
                <th style={font12}>Update Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={`${r.loanId}-${r.customerId}-${r.emiId ?? idx}`}>
                  <td style={font12}>{idx + 1}</td>
                  <td style={font12}>{r.loanId}</td>
                  <td style={font12}>{r.customerId}</td>
                  <td style={font12}>{r.emiId}</td>
                  <td style={font12}>{r.type}</td>
                  <td style={font12}>
                    {r.amount != null ? r.amount.toLocaleString() : ""}
                  </td>
                  <td style={font12}>
                    {r.outstandingAmount != null
                      ? r.outstandingAmount.toLocaleString()
                      : ""}
                  </td>
                  <td style={font12}>{r.status}</td>
                  <td style={font12}>{r.createDate}</td>
                  <td style={font12}>{r.updateDate}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
};

export default FeePIFReporting;
